// This is our http provider — all API requests go through here.
// Auth headers are attached automatically via the token registered in requestQueue.
// On 401 (expired access token), requests are queued and retried once the token is refreshed.

import { User } from '../auth/User';
import { ApiError } from './ApiError';
import { getCurrentToken, handleUnauthorized } from './requestQueue';

/** Execute a single fetch and normalise the response into data / ApiError. */
const doFetch = async (url: string, method: string, body: any, headers: Headers): Promise<any> => {
  const response = await fetch(url, {
    method,
    headers,
    // Only stringify plain objects — pass FormData / File / strings as-is
    body:
      body && !(body instanceof FormData) && !(body instanceof File) && typeof body !== 'string'
        ? JSON.stringify(body)
        : body ?? undefined,
  });

  if (response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return response.json();
    }
    return response.body;
  }

  // Non-2xx: parse error body if JSON
  const contentType = response.headers.get('content-type');
  let errorMessage = response.statusText;
  let errorDetails: any = null;

  if (contentType?.includes('application/json')) {
    try {
      const errorData = await response.json();
      errorDetails = errorData;
      errorMessage = errorData.detail || errorData.title || response.statusText;
    } catch (parseError) {
      console.error('Failed to parse error response body:', parseError);
    }
  }

  throw new ApiError({ message: errorMessage, status: response.status, details: errorDetails });
};

const HttpRequest = async (
  url: RequestInfo | URL,
  method = 'GET',
  body: any = null,
  headers: Headers | null = null,
  user: User | null = null,
): Promise<any> => {
  /**
   * Build request headers fresh on every call so that retries (after a token
   * refresh) always carry the latest access token rather than the expired one.
   */
  const buildHeaders = (): Headers => {
    const h = new Headers();
    h.append('Content-Type', 'application/json');

    // Merge any caller-supplied headers (e.g. multipart boundary overrides)
    if (headers) {
      headers.forEach((value, key) => {
        h.set(key, value);
      });
    }

    const isS3Url = url.toString().includes('.s3.') || url.toString().includes('s3.amazonaws.com');
    if (!isS3Url) {
      // Prefer the queue's registered token getter (always current);
      // fall back to the user object passed directly for legacy call-sites.
      const token = getCurrentToken() ?? user?.idToken ?? null;
      if (token) {
        h.set('Authorization', `Bearer ${token}`);
      }
    }

    return h;
  };

  // Always rebuild headers so FormData requests drop Content-Type correctly
  const effectiveHeaders = buildHeaders();
  if (body instanceof FormData) {
    effectiveHeaders.delete('Content-Type'); // let the browser set the boundary
  }

  const makeRequest = () => doFetch(url.toString(), method, body, effectiveHeaders);

  try {
    return await makeRequest();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Hand off to the queue: first caller refreshes, others wait and retry.
      return handleUnauthorized(() => {
        // Rebuild headers so the retry carries the fresh token.
        const retryHeaders = buildHeaders();
        if (body instanceof FormData) {
          retryHeaders.delete('Content-Type');
        }
        return doFetch(url.toString(), method, body, retryHeaders);
      });
    }
    throw error;
  }
};

export { HttpRequest };
